import { Session } from 'next-auth';

import { FundingType, JourneyType as JourneyTypeEnum } from '@bytel/leviator';
import { PriceDetailPosition, PriceDetailType } from '@bytel/product-wall/types';
import { Item } from '@bytel/trilogy-react/lib/components/autocomplete';
import { VariantState } from '@bytel/trilogy-react/lib/objects/facets';

import { cmsService } from '@services/cms';
import { httpService } from '@services/http';
import { HttpConfig, HttpService } from '@services/http/http';
import { odrService } from '@services/odr';
import { getDefaultPlan } from '@services/products/plan';
import { reviewsServices } from '@services/reviews';

import { getServerSession } from '@actions/get-session';

import { SapicGetOptionsType, sapicRepository } from '@repositories/sapic';

import { PalpatineProductHighlights, PalpatineProductStickers } from '@app-types/api/palpatine';
import { SapicCategoryId, SapicFundingMode, SapicProductPayload } from '@app-types/api/sapic';
import { WallType } from '@app-types/config';
import { DiscountEnumType } from '@app-types/discount';
import { Odr } from '@app-types/odr';
import { OdrProduct, Product } from '@app-types/product';
import { ReviewAverageType } from '@app-types/review';
import { SimulatorCart } from '@app-types/simulator/virtual-cart';
import { Universe } from '@app-types/universe';
import { UserType } from '@app-types/user';

import { isServer } from '@helpers/config';
import { filterValidItems } from '@helpers/content-editor';
import { localApiPaths, paths } from '@helpers/path';

import { JourneyType } from '@constants/journey';

import { getPagination } from '@product-wall/helpers';
import { format } from 'date-fns';

interface GetProductOptionType {
    plan?: string | null;
    filters?: Record<string, string[]>;
    search?: string;
    contract?: string;
    stickers?: string[];
    sort?: string;
    page?: number;
    limit?: number;
}

type MarketingData = {
    highlight?: PalpatineProductHighlights;
    stickers?: PalpatineProductStickers[];
};

class WallService {
    private httpService: HttpService;

    public constructor(httpService: HttpService) {
        this.httpService = httpService;
    }

    public async getSalesProduct(
        type: WallType.SALES_PHONE | WallType.SALES_ACCESSORY,
        options: GetProductOptionType,
        shouldBeProspect: boolean = false,
        _session?: Session,
    ): Promise<{ products: Product[]; count: number }> {
        const { products: _products, count } = await this.getProduct(
            type,
            { ...options, plan: null },
            shouldBeProspect,
            _session,
        );

        const products = _products.map((product) => {
            const stickers: Product['stickers'] = [];

            if (product.details.price.initial !== product.price) {
                product.strikethroughPrice = product.details.price.initial;
            }

            const discount = product.details.discounts.find((discount) => discount.type === DiscountEnumType.PERCENT);

            if (discount) {
                stickers.push({
                    label: `-${discount.discount}%`,
                    color: 'MAIN',
                });
            }

            product.stickers = stickers;
            return product;
        });

        return {
            products,
            count,
        };
    }

    public async getHotspotProducts(options: GetProductOptionType): Promise<{ products: Product[]; count: number }> {
        const { products: _products, count } = await this.getProduct(WallType.HOTSPOT, options, true);

        const products = _products.map((product) => {
            product.priceInfo = {
                top: 'À partir de',
                unit: '€',
            };
            return product;
        });

        return {
            products,
            count,
        };
    }

    public async getOdrProducts(options: GetProductOptionType): Promise<{ products: OdrProduct[]; count: number }> {
        try {
            const universes = await odrService.getUniverses().catch(() => []);

            let allOdrs: Odr[] = [];

            if (options.filters && Object.keys(options.filters).some((key) => key.startsWith('universe_'))) {
                const universeSlugs = [
                    ...new Set(
                        Object.keys(options.filters)
                            .filter((key) => key.startsWith('universe_'))
                            .flatMap((key) => options.filters![key]),
                    ),
                ];

                const validUniverseIds = universes.reduce((acc: string[], universe) => {
                    if (universeSlugs.includes(universe.slug)) {
                        acc.push(universe.id);
                    }
                    return acc;
                }, []);

                if (validUniverseIds.length > 0) {
                    allOdrs = await odrService.getOdrsByUniverse(validUniverseIds);
                }
            } else {
                allOdrs = await odrService.getAllOdrs();
            }

            allOdrs.sort((a, b) => {
                if (a.isExpired !== b.isExpired) {
                    return a.isExpired ? 1 : -1;
                }
                return b.startDate.getTime() - a.startDate.getTime();
            });
            allOdrs = allOdrs.filter((odr) => odr.startDate <= new Date());
            const products = allOdrs.map((odrProduct) => this.odrWallProductMapper(odrProduct, universes));

            return {
                products,
                count: allOdrs.length,
            };
        } catch (error) {
            console.error('Error fetching ODR products:', error);
            return { products: [], count: 0 };
        }
    }

    public async getProduct(
        type: WallType,
        options: GetProductOptionType,
        shouldBeProspect: boolean = false,
        _session?: Session,
    ): Promise<{ products: Product[]; count: number }> {
        const session = shouldBeProspect ? null : (_session ?? (await getServerSession()));

        if (isServer()) {
            return this.getProductServer(type, options, session);
        }

        const config: HttpConfig = {};

        if (session?.user) {
            config.user = session.user as UserType;
        }

        return this.httpService
            .get<{ products: Product[]; count: number }>(
                localApiPaths.wall,
                {
                    type,
                    options: JSON.stringify(options),
                    shouldBeProspect,
                },
                config,
            )
            .catch(() => {
                return { products: [], count: 0 };
            });
    }

    public async getSearchList(type: WallType): Promise<Item[]> {
        if (isServer()) {
            return this.getSearchListServer(type);
        }

        return this.httpService
            .get<Item[]>(localApiPaths.wallSearchList, {
                type,
            })
            .catch(() => []);
    }

    private async getProductServer(
        type: WallType,
        options: GetProductOptionType,
        session: Session | null,
    ): Promise<{ products: Product[]; count: number }> {
        const virtualCart = this.buildVirtualCart(options.contract, options.plan);

        const sapicOptions = this.buildSapicOptions(type, options);
        const [marketingDataResult, sapicResult] = await Promise.allSettled([
            this.getCmsInfo(type, options.contract !== undefined),
            sapicRepository.getProducts(virtualCart, sapicOptions, session),
        ]);

        if (sapicResult.status === 'rejected') {
            return { products: [], count: 0 };
        }

        const marketingData = marketingDataResult.status === 'fulfilled' ? marketingDataResult.value : {};

        const count = sapicResult.value.count;
        // DIGFACT-82377 : Filter products without funding in case SAPIC returns products not configured as incompatible
        // Note: This filtering may create inconsistencies with SAPIC response and break pagination
        const products = sapicResult.value.products.filter((value) => value.details.fundings.length > 0);

        const reviews = await this.getReviews(products);

        return {
            products: products.map((product) =>
                this.wallProductMapper(
                    type,
                    product,
                    options.contract !== undefined,
                    marketingData[product.urlKey],
                    reviews[product.gencode],
                ),
            ),
            count,
        };
    }

    private async getSearchListServer(type: WallType): Promise<Item[]> {
        const virtualCart = this.buildVirtualCart();
        const options = this.buildSapicOptions(type, { limit: -1 });

        return sapicRepository
            .getProducts(virtualCart, options)
            .then(({ products }) =>
                products.map((product) => ({
                    data: `${product.brand} ${product.name}`,
                    label: `${product.brand} ${product.name}`,
                })),
            )
            .then((products) => products.sort((a, b) => a.data.localeCompare(b.data)))
            .catch(() => []);
    }

    private buildVirtualCart(contractId?: string, mobilePlan?: string | null): SimulatorCart {
        const isRenewal = contractId !== undefined;

        if (mobilePlan === undefined || (isRenewal && !mobilePlan)) {
            mobilePlan = getDefaultPlan(isRenewal, [], false).toString();
        }

        return {
            cart: {
                quotes: [
                    {
                        type: isRenewal ? JourneyTypeEnum.RENEWAL : JourneyTypeEnum.MOBILE_ACQUISITION,
                        isCurrent: true,
                        products: mobilePlan ? [{ gencode: mobilePlan }] : [],
                        contractId,
                    },
                ],
            },
        };
    }

    private buildSapicOptions(type: WallType, options: GetProductOptionType): SapicGetOptionsType {
        let paginationData;

        if (options.page && options.page > 0 && options.limit) {
            const stickersPosition =
                options.stickers?.map((sticker) => {
                    return Number.parseInt(sticker);
                }) ?? [];

            paginationData = getPagination(options.page, options.limit, stickersPosition);
        }

        const limit = paginationData?.limit ?? options.limit;
        const offset = paginationData?.offset ?? 0;
        const sort = options.sort ?? 'meilleures-ventes';

        let category: SapicCategoryId;

        switch (type) {
            case WallType.PHONE:
                category = SapicCategoryId.PHONE;
                break;
            case WallType.ACCESSORY:
                category = SapicCategoryId.ACCESSORY;
                break;
            case WallType.SALES_PHONE:
                category = SapicCategoryId.SALES_PHONE;
                break;
            case WallType.SALES_ACCESSORY:
                category = SapicCategoryId.SALES_ACCESSORY;
                break;
            case WallType.HOTSPOT:
                category = SapicCategoryId.HOTSPOT;
                break;
            default:
                throw new Error(`Unsupported wall type: ${type}`);
        }

        const searchParams: Record<string, string> = {};

        if (options.search) {
            searchParams.nom = options.search;
        }

        if (options.filters) {
            const sapicFilters = { ...options.filters };
            delete sapicFilters['sousType'];

            Object.entries(sapicFilters).forEach((filtersData) => {
                const key = filtersData[0] as keyof SapicProductPayload;
                if (key === 'prix') {
                    searchParams[key] = this.getPriceRange(filtersData[1]).join('-');
                    return;
                }

                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                searchParams[key] = filtersData[1].join(',');
            });
        }

        return {
            category,
            searchParams,
            fundingMode: SapicFundingMode.AUTO,
            limit,
            offset,
            sort,
            contract: options.contract,
        };
    }

    private async getReviews(products: Product[]): Promise<Record<string, ReviewAverageType>> {
        const gencodes = products.map((product) => {
            return product.gencode;
        });

        if (gencodes.length > 0) {
            const reviews = await reviewsServices.getAverageReviews(gencodes);

            return (reviews ?? []).reduce((acc: Record<string, ReviewAverageType>, review) => {
                acc[review.gencode] = review;
                return acc;
            }, {});
        }

        return {};
    }

    private async getCmsInfo(type: WallType, isRenewal: boolean): Promise<Record<string, MarketingData>> {
        try {
            const { items } = await (type === WallType.PHONE
                ? cmsService.getProduct()
                : Promise.resolve({ items: [] }));

            const marketingData: Record<string, MarketingData> = {};

            items.forEach(({ Slug, highlight = { items: [] }, stickers = { items: [] } }) => {
                const journeyType = isRenewal ? JourneyType.RENEWAL : JourneyType.ACQUISITION;
                const hightlightsValue = highlight.items.filter((datum) =>
                    filterValidItems(journeyType, datum.parcours, datum.start_date, datum.end_date),
                );
                const stickersValues = stickers.items.filter((datum) =>
                    filterValidItems(journeyType, datum.parcours, datum.start_date, datum.end_date),
                );

                marketingData[Slug] = {
                    highlight: hightlightsValue[0],
                    stickers: stickersValues,
                };
            });

            return marketingData;
        } catch {
            return {};
        }
    }

    private wallProductMapper(
        type: WallType,
        product: Product,
        isRenewal: boolean,
        marketingData: MarketingData = {},
        review?: ReviewAverageType,
    ): Product {
        let stickers: Product['stickers'];

        const { highlight, stickers: _stickers } = marketingData;

        if (type === WallType.PHONE) {
            stickers =
                _stickers?.map((sticker) => ({
                    label: sticker.sticker_text,
                    color: sticker.sticker_color as VariantState,
                })) ?? [];
        } else {
            stickers = product.stickers;
        }

        const funding = product.details.fundings?.[0];

        const isFunding = funding !== undefined && funding.type !== FundingType.COMPTANT;
        let instalment: Product['instalment'] | undefined;

        if (isFunding) {
            instalment = {
                amount: funding.monthlyAmount,
                count: funding?.installments ?? 0,
            };
        }

        const initialDeposit = funding?.initialDeposit ?? 0;
        const hasCashbackOffer =
            product.details.discounts.some(({ type }) => type === DiscountEnumType.ODR) && initialDeposit > 1;
        const instantDiscount =
            (product.details.price.subsidized || product.details.price.initial) - product.details.price.final;

        return {
            ...product,
            url: paths.productDetail(type, product.url, isRenewal),
            instalment,
            header: product.recommended ? 'Recommandé pour vous' : undefined,
            priceDetail: this.getPriceDetail(isFunding, instantDiscount, hasCashbackOffer),
            rating: review
                ? {
                      value: review.rate,
                      count: review.count,
                  }
                : undefined,
            stickers,
            stamp: highlight?.stamp?.public_url
                ? {
                      url: highlight.stamp.public_url,
                      alt: highlight.stamp_alt ?? '',
                  }
                : undefined,
            _marketingMeta: {
                stamp: highlight?.phone_wall_plan_target
                    ? {
                          displayWithPlan: highlight.phone_wall_plan_target.includes('with_plan'),
                          displayWithoutPlan: highlight.phone_wall_plan_target.includes('no_plan'),
                      }
                    : undefined,
                stickers: _stickers?.map((s) =>
                    s.phone_wall_plan_target
                        ? {
                              displayWithPlan: s.phone_wall_plan_target.includes('with_plan'),
                              displayWithoutPlan: s.phone_wall_plan_target.includes('no_plan'),
                          }
                        : undefined,
                ),
            },
        };
    }

    private odrWallProductMapper(odrProduct: Odr, universes: Universe[] = []): OdrProduct {
        const startDate = format(odrProduct.startDate, 'dd/MM/yyyy');
        const endDate = format(odrProduct.endDate, 'dd/MM/yyyy');

        return {
            id: odrProduct.id,
            gencode: odrProduct.id,
            name: odrProduct.title,
            urlKey: odrProduct.id,
            url: odrProduct.id,
            image: odrProduct.imageUrl,
            description: `Date d’achat : du ${startDate} au ${endDate}`,
            price: odrProduct.refundAmount,
            startDate,
            endDate,
            priceInfo: {
                top: odrProduct.isUntil ? "Jusqu'à" : undefined,
                unit:
                    Boolean(odrProduct.duration) || (!odrProduct.isPercent && !odrProduct.notMoney)
                        ? '€'
                        : odrProduct.isPercent
                          ? '%'
                          : '',
                bottom: odrProduct.duration ? `pendant ${odrProduct.duration} mois` : odrProduct.notMoney,
            },
            priceDetail: odrProduct.annotation
                ? {
                      type: PriceDetailType.POPOVER,
                      position: PriceDetailPosition.BESIDE,
                      text: odrProduct.annotation,
                  }
                : undefined,
            recommended: false,
            inStock: !odrProduct.isExpired,
            categories: [],
            details: {
                price: {
                    initial: odrProduct.refundAmount,
                    final: odrProduct.refundAmount,
                    subsidized: odrProduct.refundAmount,
                    forever: odrProduct.refundAmount,
                },
                fundings: [],
                discounts: [],
            },
            header: universes.find((universe) => universe.id === odrProduct.universeId)?.name,
            stickers: odrProduct.isExpired ? [{ label: 'Offre terminée', color: 'MAIN' }] : undefined,
        };
    }

    private getPriceDetail(
        isFunding: boolean,
        instantDiscount: number,
        hasCashbackOffer: boolean,
    ): Product['priceDetail'] | undefined {
        if (hasCashbackOffer) {
            return {
                type: PriceDetailType.BUTTON,
                position: PriceDetailPosition.BELOW,
                text: 'après remboursement',
            };
        }

        if (instantDiscount > 0) {
            return {
                type: PriceDetailType.BUTTON,
                position: PriceDetailPosition.BELOW,
                text: 'après remise',
            };
        }

        if (isFunding) {
            return {
                type: PriceDetailType.BUTTON,
                position: PriceDetailPosition.BESIDE,
            };
        }
    }

    private getPriceRange(priceRangeList: string[]): [number, number | undefined] {
        const defaultMin = Infinity;
        const defaultMax = -Infinity;

        const minMax = priceRangeList.reduce(
            ([min, max]: [number, number], currentValue): [number, number] => {
                let [currentMin, currentMax] = currentValue.split('-').map((value) => Number.parseInt(value)) as [
                    number,
                    number,
                ];

                currentMin = isNaN(currentMin) ? -Infinity : currentMin;
                currentMax = isNaN(currentMax) ? Infinity : currentMax;

                min = min < currentMin ? min : currentMin;
                max = max > currentMax ? max : currentMax || Infinity;

                return [min, max];
            },
            [defaultMin, defaultMax],
        );

        const min = minMax[0] === -Infinity ? 0 : minMax[0];
        const max = minMax[1] === Infinity ? undefined : minMax[1];

        return [min, max];
    }
}

export const wallService = new WallService(httpService);
